/**
 * Which meals on Today have their nutrition open, remembered on this device so
 * they stay open across days, visits and reloads. Only meal names are stored.
 * Without storage (blocked, private browsing, tests) nothing is remembered and
 * every meal starts closed.
 */
import { MEALS, type Meal } from '../../shared/dates';

export const OPEN_MEALS_KEY = 'grams.openMeals';

/** The part of Web Storage used here, so tests can pass their own. */
export interface MealStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function deviceStore(): MealStore | undefined {
  try {
    return (globalThis as { localStorage?: MealStore }).localStorage;
  } catch {
    // Reading localStorage itself throws where storage is blocked.
    return undefined;
  }
}

/** The meals a stored value names, in meal order. Anything else in it is ignored. */
export function parseOpenMeals(raw: string | null): Meal[] {
  if (!raw) return [];
  try {
    const value: unknown = JSON.parse(raw);
    return Array.isArray(value) ? MEALS.filter((m) => value.includes(m)) : [];
  } catch {
    return [];
  }
}

/** The open meals with one meal opened or closed. A new set: the one given is left as it was. */
export function toggleMeal(open: ReadonlySet<Meal>, meal: Meal): Set<Meal> {
  const next = new Set(open);
  if (!next.delete(meal)) next.add(meal);
  return next;
}

export function readOpenMeals(store: MealStore | undefined = deviceStore()): Meal[] {
  try {
    return parseOpenMeals(store?.getItem(OPEN_MEALS_KEY) ?? null);
  } catch {
    return [];
  }
}

export function writeOpenMeals(open: Iterable<Meal>, store: MealStore | undefined = deviceStore()): void {
  const chosen = new Set(open);
  const meals = MEALS.filter((m) => chosen.has(m));
  try {
    if (meals.length > 0) store?.setItem(OPEN_MEALS_KEY, JSON.stringify(meals));
    else store?.removeItem(OPEN_MEALS_KEY);
  } catch {
    // Full or blocked storage: the meals stay open for this visit, just not remembered.
  }
}
